import PropTypes from "prop-types"
import React, { useMemo } from "react"
import { useMarina } from "src/contexts/MarinaContext"
import { itemDatesAndSubtext } from "src/main/Billing/Items/helpers"
import {
  collapseReservationItems,
  getPaymentItemEndDate,
  getPaymentItemName,
  getPaymentItemStartDate,
  getTotalBalance,
  groupRelatedPaymentItemRows,
  isProductReturnRow,
} from "src/main/Billing/Payments/helpers"
import { sortItems } from "src/main/Billing/helpers"

import DataTableV2 from "src/components/DataTableV2"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

const shouldStrikethrough = (item) =>
  !item.txn.product_sale?.reservation_sale && isProductReturnRow(item)

const InvoiceItemsTable = ({ items, isPaymentSettled }) => {
  const { presentmentCurrency } = useMarina()
  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })
  const amountHeader = isPaymentSettled
    ? "Transaction Amount"
    : "Scheduled Amount"

  const rows = useMemo(() => {
    const collapsed = (sortItems(collapseReservationItems(items)) ?? []).filter(
      (i) => i.amount !== 0
    )
    return Object.entries(groupRelatedPaymentItemRows(collapsed)).map(
      ([id, item]) => {
        const [dates] = itemDatesAndSubtext({
          startDate: getPaymentItemStartDate(item),
          endDate: getPaymentItemEndDate(item),
          monthToMonth: false,
        })
        return { id, name: getPaymentItemName(item), dates, item }
      }
    )
  }, [items])

  const strikeClass = (row) =>
    shouldStrikethrough(row.item) ? "line-through" : undefined

  const moneyCol = { cellAlign: "right", headerAlign: "right", width: "110px" }

  const columns = [
    {
      key: "name",
      header: "Name",
      width: "240px",
      render: (r) => <span className={strikeClass(r)}>{r.name}</span>,
    },
    {
      key: "dates",
      header: "Dates",
      width: "140px",
      render: (r) => <span className={strikeClass(r)}>{r.dates}</span>,
    },
    {
      key: "subtotal",
      header: "Subtotal",
      ...moneyCol,
      render: (r) => (
        <span className={strikeClass(r)}>{money(r.item.subtotal)}</span>
      ),
    },
    {
      key: "discount",
      header: "Discount",
      ...moneyCol,
      render: (r) => (
        <span className={strikeClass(r)}>{money(r.item.discount)}</span>
      ),
    },
    {
      key: "tax",
      header: "Tax",
      ...moneyCol,
      render: (r) => (
        <span className={strikeClass(r)}>{money(r.item.tax)}</span>
      ),
    },
    {
      key: "totalPrice",
      header: "Total Price",
      ...moneyCol,
      render: (r) => (
        <span className={strikeClass(r)}>
          {money(Math.abs(r.item.txn.amount))}
        </span>
      ),
    },
    {
      key: "amount",
      header: amountHeader,
      ...moneyCol,
      width: "170px",
      render: (r) => money(r.item.amount),
    },
  ]

  return (
    <DataTableV2
      name="invoice-items-table"
      columns={columns}
      data={rows}
      hideActions
      hideSettings
      maxHeight="none"
      footerRow={{ name: "Total", amount: money(getTotalBalance(items)) }}
      emptyState={
        <span className="text-gray-500">No products / services to display</span>
      }
    />
  )
}

InvoiceItemsTable.propTypes = {
  items: PropTypes.array.isRequired,
  isPaymentSettled: PropTypes.bool.isRequired,
}

export default InvoiceItemsTable
